/**
 * Área COMPROMISSOS: o calendário semanal do admin e o modal de compromisso.
 *
 * Cobre as sete chamadas ao Supabase do calendário no legado (`admin.html:4326-4706`), cada uma
 * com a mesma consulta: a lista de leads do campo "Lead", a semana de compromissos, o lead do
 * atalho "Ir para o cliente", criar, atualizar e excluir compromisso, e o e-mail de confirmação.
 *
 * O QUE NÃO ESTÁ AQUI, e por quê: o lembrete por e-mail ("Avisar por e-mail") não é enviado pelo
 * admin. O legado só grava `avisar_antes_minutos` e zera `lembrete_enviado_em`; quem envia é uma
 * Edge Function com cron, fora do `admin.html` (comentário em `admin.html:4327-4332`).
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import {
  clienteDe,
  executar,
  invocarFuncao,
  type FonteDeDados,
  type LinhaCompromissosAgenda,
  type LinhaLeadsFunil,
  type Resultado,
} from '../nucleo.js';

/** O lead como o campo "Lead" do modal o lê: só as quatro colunas que o legado pede. */
export type LeadParaCompromisso = Pick<LinhaLeadsFunil, 'id' | 'nome' | 'email' | 'status'>;

/**
 * Lê todos os leads para o campo "Lead (opcional)", em ordem de nome.
 * Legado: `admin.html:4365`, `sb.from('leads_funil').select('id, nome, email, status').order('nome')`.
 *
 * Sem filtro de status de propósito: o comentário do legado (`admin.html:4327`) diz que o
 * compromisso pode ser de lead finalizado ou perdido, como lembrete de reativação.
 */
export function listarLeadsParaCompromisso(fonte: FonteDeDados): Promise<Resultado<LeadParaCompromisso[]>> {
  // A mesma consulta do legado: quatro colunas, ordem alfabética pelo nome.
  return executar<LeadParaCompromisso[]>(clienteDe(fonte).from('leads_funil').select('id, nome, email, status').order('nome'));
}

/** O compromisso como a grade o lê: as colunas pedidas e o lead embutido pela chave estrangeira. */
export type CompromissoDaSemana = Pick<
  LinhaCompromissosAgenda,
  | 'id'
  | 'lead_id'
  | 'titulo'
  | 'descricao'
  | 'data_hora_inicio'
  | 'duracao_minutos'
  | 'avisar_antes_minutos'
  | 'pedir_confirmacao_cliente'
  | 'confirmacao_cliente'
  | 'confirmacao_respondida_em'
> & {
  // O PostgREST devolve o lead embutido como objeto (relação muitos-para-um), ou `null` sem lead.
  readonly leads_funil: Pick<LinhaLeadsFunil, 'nome' | 'status' | 'contato_whatsapp'> | null;
};

// As colunas exatas do `select` do legado, numa constante para a consulta e o teste lerem igual.
const COLUNAS_DA_SEMANA =
  'id, lead_id, titulo, descricao, data_hora_inicio, duracao_minutos, avisar_antes_minutos, pedir_confirmacao_cliente, confirmacao_cliente, confirmacao_respondida_em, leads_funil(nome, status, contato_whatsapp)';

/**
 * Lê os compromissos de um intervalo, do início (incluído) ao fim (excluído), em ordem de início.
 * Legado: `admin.html:4380-4385`, `.gte('data_hora_inicio', inicio).lt('data_hora_inicio', fim)
 * .order('data_hora_inicio')`.
 *
 * O intervalo chega pronto em ISO (o `toISOString` do legado), porque quem sabe onde a semana
 * começa é a tela, pela hora local; aqui só se repete a consulta.
 */
export function listarCompromissosDoPeriodo(
  fonte: FonteDeDados,
  inicioIso: string,
  fimIso: string,
): Promise<Resultado<CompromissoDaSemana[]>> {
  return executar<CompromissoDaSemana[]>(
    clienteDe(fonte)
      .from('compromissos_agenda')
      // As mesmas colunas, com o lead embutido.
      .select(COLUNAS_DA_SEMANA)
      // Início incluído: o compromisso do domingo à meia-noite entra na semana.
      .gte('data_hora_inicio', inicioIso)
      // Fim excluído: o do domingo seguinte à meia-noite fica para a próxima semana.
      .lt('data_hora_inicio', fimIso)
      // Em ordem de início, como a grade desenha.
      .order('data_hora_inicio'),
  );
}

/**
 * Lê o lead inteiro para o atalho "Ir para o cliente" do evento.
 * Legado: `admin.html:4540`, `sb.from('leads_funil').select('*').eq('id', leadId).maybeSingle()`.
 *
 * `dados` nulo é o lead que não existe mais: a tela diz "Erro ao carregar o lead.", como o legado
 * diz para os dois casos (`error || !lead`).
 */
export function lerLeadDoCompromisso(fonte: FonteDeDados, leadId: number): Promise<Resultado<LinhaLeadsFunil | null>> {
  // `select('*')` como o legado, porque a ficha que abre em seguida usa o lead inteiro.
  return executar<LinhaLeadsFunil | null>(clienteDe(fonte).from('leads_funil').select('*').eq('id', leadId).maybeSingle());
}

/** O que o botão "Salvar" grava, campo por campo como o `registro` de `admin.html:4636-4645`. */
export interface RegistroDeCompromisso {
  // O lead escolhido, ou `null` no compromisso manual.
  readonly lead_id: number | null;
  // O título digitado, ou `null` quando ficou vazio (vale o nome do lead).
  readonly titulo: string | null;
  // As observações, ou `null` quando vazias.
  readonly descricao: string | null;
  // Data e hora locais convertidas para ISO, como `new Date(...).toISOString()` do legado.
  readonly data_hora_inicio: string;
  // A duração escolhida, em minutos.
  readonly duracao_minutos: number;
  // O aviso por e-mail, em minutos antes, ou `null` para "Não avisar".
  readonly avisar_antes_minutos: number | null;
  // Sempre `null` ao salvar: o lembrete precisa ser reenviado para o horário novo.
  readonly lembrete_enviado_em: null;
  // Pedir confirmação só existe com lead; sem lead o legado grava `false`.
  readonly pedir_confirmacao_cliente: boolean;
  // Só na edição que mudou data, hora, título ou duração: a resposta anterior deixa de valer.
  readonly confirmacao_cliente?: null;
  // Idem, zerada junto com a resposta.
  readonly confirmacao_respondida_em?: null;
}

/**
 * Cria um compromisso e devolve o `id` criado, que o e-mail de confirmação precisa.
 * Legado: `admin.html:4675`, `sb.from('compromissos_agenda').insert(registro).select('id').single()`.
 *
 * O `.single()` depois do `insert` é o certo: a linha acabou de ser criada e tem de voltar.
 */
export function criarCompromisso(
  fonte: FonteDeDados,
  registro: RegistroDeCompromisso,
): Promise<Resultado<Pick<LinhaCompromissosAgenda, 'id'>>> {
  return executar<Pick<LinhaCompromissosAgenda, 'id'>>(
    clienteDe(fonte).from('compromissos_agenda').insert(registro).select('id').single(),
  );
}

/**
 * Atualiza um compromisso existente, sem ler nada de volta.
 * Legado: `admin.html:4674`, `sb.from('compromissos_agenda').update(registro).eq('id', id)`.
 */
export function atualizarCompromisso(
  fonte: FonteDeDados,
  id: number,
  registro: RegistroDeCompromisso,
): Promise<Resultado<null>> {
  // Sem `select`, o PostgREST responde sem corpo, e `dados` volta `null`.
  return executar<null>(clienteDe(fonte).from('compromissos_agenda').update(registro).eq('id', id));
}

/**
 * Exclui um compromisso.
 * Legado: `admin.html:4702`, `sb.from('compromissos_agenda').delete().eq('id', id)`.
 */
export function excluirCompromisso(fonte: FonteDeDados, id: number): Promise<Resultado<null>> {
  return executar<null>(clienteDe(fonte).from('compromissos_agenda').delete().eq('id', id));
}

/** O corpo do e-mail de confirmação, com os sete campos que o legado manda (`admin.html:4682-4690`). */
export interface CorpoConfirmacaoCompromisso {
  // O compromisso que a cliente vai confirmar ou recusar.
  readonly compromisso_id: number;
  // Para onde vai o e-mail: o e-mail do lead.
  readonly cliente_email: string;
  // Como o e-mail chama a cliente: o nome do lead.
  readonly cliente_nome: string;
  // O título gravado, que pode ser `null` (compromisso só com lead).
  readonly titulo: string | null;
  // O início em ISO, como foi gravado.
  readonly data_hora_inicio: string;
  // A duração em minutos.
  readonly duracao_minutos: number;
  // As observações, que podem ser `null`.
  readonly descricao: string | null;
}

/**
 * Pede à Edge Function o e-mail de confirmação para a cliente.
 * Legado: `admin.html:4681`, `sb.functions.invoke('notificar-compromisso-confirmacao', { body })`.
 *
 * O legado não espera a resposta nem mostra falha (só `console.warn`); a tela nova faz o mesmo,
 * e esta função devolve o `Resultado` para quem quiser registrar.
 */
export function notificarConfirmacaoCompromisso(
  fonte: FonteDeDados,
  corpo: CorpoConfirmacaoCompromisso,
): Promise<Resultado<unknown>> {
  // O corpo vai como veio, com os mesmos nomes de campo que a função lê.
  return invocarFuncao<unknown>(fonte, 'notificar-compromisso-confirmacao', { body: { ...corpo } });
}
